// ============================================================================
// Smarter rule-based intent matcher
// ----------------------------------------------------------------------------
// Goals:
//   1. Accuracy over verbosity. If we are reasonably confident, return the
//      best intent. If we are NOT confident, return null and let the UI show
//      the friendly fallback (which points to GitHub + email).
//   2. Handle natural phrasings — conversational prefixes, synonyms, simple
//      typos, plurals, common variations.
//   3. Tiebreak by intent specificity (longer keywords + higher priority win).
// ----------------------------------------------------------------------------

import { intents } from './knowledge';

// Words that carry little intent signal and should not score on their own.
const STOPWORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'of', 'on', 'in', 'to', 'for', 'with', 'and', 'or', 'but', 'so',
  'i', 'me', 'my', 'mine', 'you', 'your', 'yours', 'he', 'his', 'him',
  'she', 'her', 'we', 'our', 'they', 'them', 'their', 'it', 'its',
  'do', 'does', 'did', 'have', 'has', 'had', 'can', 'could', 'would',
  'should', 'will', 'shall', 'may', 'might', 'must', 'just',
  'this', 'that', 'these', 'those',
  'about', 'tell', 'show', 'know', 'want', 'wanna', 'like',
  'please', 'kindly', 'thanks',
]);

// Conversational prefixes we strip before scoring so they do not dominate.
const CONVERSATIONAL_PREFIXES = [
  'can you please tell me about',
  'can you please tell me',
  'can you tell me about',
  'can you tell me',
  'could you tell me about',
  'could you tell me',
  'please tell me about',
  'please tell me',
  'i want to know about',
  'i would like to know about',
  'i wanna know about',
  'i need to know about',
  'i need to know',
  'do you know about',
  'do you know',
  'tell me about',
  'tell me',
  'show me his',
  'show me',
  'give me info on',
  'give me information on',
  'give me details on',
  'what about',
  'how about',
  'whats up with',
  'how can you tell me about',
];

// Light synonym normalization — collapses common variants to a single token.
const SYNONYM_MAP = {
  'cv': 'resume',
  'work history': 'experience',
  'tech stack': 'stack',
  'a.i': 'ai',
  'm.l': 'ml',
  'github profile': 'github',
  'tharak\'s': 'tharak',
  'tharaks': 'tharak',
  'he\'s': 'he is',
  'whats': 'what is',
  'whos': 'who is',
  'hows': 'how is',
  'gonna': 'going to',
  'wanna': 'want to',
};

// ---------------------------------------------------------------------------
// Text utilities
// ---------------------------------------------------------------------------

const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const stripConversationalPrefix = (text) => {
  let out = text;
  // Strip up to 2 times in case of stacked prefixes.
  for (let pass = 0; pass < 2; pass++) {
    let stripped = false;
    for (const p of CONVERSATIONAL_PREFIXES) {
      if (out === p) return ''; // Pure prefix only → handled by caller
      if (out.startsWith(p + ' ')) {
        out = out.slice(p.length).trim();
        stripped = true;
        break;
      }
    }
    if (!stripped) break;
  }
  return out;
};

const applySynonyms = (text) => {
  let out = text;
  for (const [from, to] of Object.entries(SYNONYM_MAP)) {
    if (out.includes(from)) {
      out = out.split(from).join(to);
    }
  }
  return out;
};

// Singular-ize a token (very light — only handles common plurals).
const singular = (tok) => {
  if (tok.length > 4 && tok.endsWith('ies')) return tok.slice(0, -3) + 'y';
  if (tok.length > 3 && tok.endsWith('es') && !tok.endsWith('ses')) return tok.slice(0, -2);
  if (tok.length > 3 && tok.endsWith('s') && !tok.endsWith('ss')) return tok.slice(0, -1);
  return tok;
};

// Compact Levenshtein distance — used only on shortish words to catch typos.
const levenshtein = (a, b) => {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  if (Math.abs(al - bl) > 2) return 99; // cheap reject

  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    for (let j = 1; j <= bl; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
};

const fuzzyMatch = (token, keyword) => {
  if (token === keyword) return true;
  if (token.length < 4 || keyword.length < 4) return false;
  // Allow 1 edit for 4-6 char words, 2 edits for 7+ char words.
  const allowed = keyword.length >= 7 ? 2 : 1;
  return levenshtein(token, keyword) <= allowed;
};

// ---------------------------------------------------------------------------
// Core matching
// ---------------------------------------------------------------------------

const tokenize = (text) =>
  text
    .split(' ')
    .filter((t) => t.length > 1 && !STOPWORDS.has(t))
    .map(singular);

const scoreIntent = (intent, text, tokens, tokenSet) => {
  let score = 0;
  let hits = 0;
  let bestKwLen = 0;

  for (const kwRaw of intent.keywords) {
    const kw = kwRaw.toLowerCase();
    if (kw.length < 2) continue;

    if (kw.includes(' ')) {
      // Multi-word phrase — substring match in full text
      if (text.includes(kw)) {
        const words = kw.split(' ').length;
        // Phrases worth more the longer they are
        score += 3 + words;
        hits += 1;
        if (kw.length > bestKwLen) bestKwLen = kw.length;
      }
    } else {
      const kwSing = singular(kw);
      // Exact token match (singular-aware)
      if (tokenSet.has(kw) || tokenSet.has(kwSing)) {
        score += 3;
        hits += 1;
        if (kw.length > bestKwLen) bestKwLen = kw.length;
      } else if (kw.length >= 5 && text.includes(kw)) {
        // Long-keyword substring (e.g. 'publication' inside 'publications-list')
        score += 2;
        hits += 1;
        if (kw.length > bestKwLen) bestKwLen = kw.length;
      } else {
        // Typo tolerance — fuzzy match against any input token
        for (const tok of tokens) {
          if (fuzzyMatch(tok, kw) || fuzzyMatch(tok, kwSing)) {
            score += 2;
            hits += 1;
            if (kw.length > bestKwLen) bestKwLen = kw.length;
            break;
          }
        }
      }
    }
  }

  // Diversity bonus: multiple distinct keyword hits
  if (hits >= 2) score += 1;
  if (hits >= 3) score += 1;

  // Specificity bonus — intents with longer matched keyword win ties
  const specificity = bestKwLen + (intent.priority || 0) * 2;

  return { score, hits, specificity };
};

export const matchIntent = (input) => {
  const raw = normalize(input);
  if (!raw) return null;

  const expanded = applySynonyms(raw);
  let text = stripConversationalPrefix(expanded);
  // If the user typed only a conversational prefix like "tell me about" with
  // nothing after, treat it as a general about question.
  if (!text) {
    return intents.find((i) => i.id === 'about') || null;
  }

  const tokens = tokenize(text);
  const tokenSet = new Set(tokens);
  // Include singular forms in the lookup set
  for (const t of tokens) tokenSet.add(singular(t));

  let best = null;
  let bestScore = 0;
  let bestSpec = 0;

  for (const intent of intents) {
    const { score, specificity } = scoreIntent(intent, text, tokens, tokenSet);
    if (score > bestScore || (score === bestScore && specificity > bestSpec)) {
      bestScore = score;
      bestSpec = specificity;
      best = intent;
    }
  }

  // Confidence threshold — be a little permissive so natural phrasings still
  // land on a real intent. Below this, we'd rather return a friendly fallback
  // than guess wrong (accuracy > verbosity).
  if (bestScore < 2) return null;
  return best;
};

export const replyFor = (input) => {
  const intent = matchIntent(input);
  if (!intent) return null; // UI will use FALLBACK
  return intent;
};
